import { Link } from '../../../components/Link';
import { Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';
import { useLegalPagesVisible } from '../../../lib/useLegalPagesVisible';

/**
 * Data Processing Agreement — structured on GDPR Art. 28 + the Stripe DPA template
 * (https://stripe.com/legal/dpa). Customer is Controller; SieveGuard is Processor for any
 * email data submitted to the Service.
 */
export function DpaPage() {
  const c = useLegal();
  const { isVisible } = useLegalPagesVisible();
  const subprocessorsPage = isVisible('subprocessors');
  // Section 5 is the Sub-processors clause. When it's hidden the sections
  // downstream close the gap so the DPA doesn't look like it dropped a
  // clause by accident (§4 → §6). The sub-processors clause is the ONLY
  // conditionally-hidden section, so a single offset covers every later
  // heading — no per-section conditional needed.
  const n = (num: number) => (subprocessorsPage ? num : num - 1);
  return (
    <><Seo title="Data Processing Agreement" description="GDPR Article 28 agreement between you as Controller and SieveGuard as Processor — scope of processing, security measures, sub-processors, and transfers." path="/dpa" /><LegalLayout title="Data Processing Agreement" lastUpdated={c.lastUpdatedAt}>
      <p>
        This Data Processing Agreement (&quot;<strong>DPA</strong>&quot;) supplements our{' '}
        <Link to="/terms">Terms of Use</Link> and applies whenever{' '}
        <strong>{c.legalEntityName}</strong> (&quot;<strong>SieveGuard</strong>&quot;,
        &quot;<strong>Processor</strong>&quot;) processes personal data on behalf of
        you (&quot;<strong>Customer</strong>&quot;, &quot;<strong>Controller</strong>&quot;)
        under the EU GDPR or UK GDPR.
      </p>

      <h2>1. Definitions</h2>
      <p>
        Terms not defined here have the meanings given in the GDPR. &quot;Personal Data&quot;,
        &quot;Processing&quot;, &quot;Data Subject&quot;,
        {subprocessorsPage && <> &quot;Sub-processor&quot;,</>}{' '}
        &quot;Supervisory Authority&quot;, &quot;Personal Data Breach&quot; bear their GDPR
        meanings.
      </p>

      <h2>2. Scope and roles</h2>
      <p>
        You are the Controller of Personal Data you submit to the Service. SieveGuard is the
        Processor. This DPA applies to all Processing carried out under your Terms of Use.
      </p>

      <h2>3. Processing only on documented instructions</h2>
      <p>
        SieveGuard processes Personal Data only on your documented instructions, including any
        configuration you make in the SieveGuard console and any API requests you submit. We
        will not process Personal Data for any other purpose without your prior written
        consent.
      </p>

      <h2>4. Confidentiality of personnel</h2>
      <p>
        All personnel authorised to process Personal Data are bound by appropriate
        confidentiality obligations.
      </p>

      {subprocessorsPage && (
        <>
          <h2>5. Sub-processors</h2>
          <p>
            You authorise SieveGuard to engage Sub-processors to provide the Service. We impose data
            protection terms on each Sub-processor offering at least the same level of protection
            as this DPA, and remain liable to you for their performance. The current list is at{' '}
            <Link to="/subprocessors">/subprocessors</Link>; we notify you by email at least 30
            days before adding or replacing a Sub-processor, and you may object on reasonable
            grounds, in which case we will work in good faith to find a remedy or you may
            terminate your subscription.
          </p>
        </>
      )}

      <h2>{n(6)}. Data subject rights assistance</h2>
      <p>
        Taking into account the nature of the Processing, we assist you by appropriate
        technical and organisational measures, insofar as possible, for the fulfilment of
        your obligation to respond to requests from data subjects (access, rectification,
        erasure, restriction, portability, objection).
      </p>

      <h2>{n(7)}. Security</h2>
      <p>
        SieveGuard implements appropriate technical and organisational measures to ensure a level
        of security appropriate to the risk — see <strong>Annex II</strong>.
      </p>

      <h2>{n(8)}. Personal data breach</h2>
      <p>
        SieveGuard notifies you without undue delay, and in any event within 72 hours, after
        becoming aware of a Personal Data Breach affecting your data. The notification
        describes the nature of the breach, the categories and approximate number of data
        subjects and records concerned, likely consequences, and the measures taken or
        proposed.
      </p>

      <h2>{n(9)}. Data protection impact assessments</h2>
      <p>
        On request and at reasonable cost, we assist you with data protection impact
        assessments and prior consultations with Supervisory Authorities.
      </p>

      <h2>{n(10)}. Return or deletion at end of services</h2>
      <p>
        At the end of your Subscription, we delete all Personal Data within{' '}
        <strong>{c.retentionDaysAfterTermination} days</strong>, unless EU or member-state
        law requires retention. You may request immediate purge at any time from the portal
        or by email to <code>{c.privacyEmail}</code>.
      </p>

      <h2>{n(11)}. Audits</h2>
      <p>
        We make available to you all information necessary to demonstrate compliance with
        this DPA. Once per year, on 30 days&apos; written notice, you may request a remote
        audit conducted at your reasonable expense, subject to confidentiality undertakings.
        Where available, we may satisfy this obligation by providing a current SOC 2 or ISO
        27001 report.
      </p>

      <h2>{n(12)}. International transfers</h2>
      <p>
        Where personal data is transferred outside the EEA / UK, the EU Standard Contractual
        Clauses (Commission Implementing Decision 2021/914) are incorporated by reference
        and apply between the parties.
      </p>

      <h2>{n(13)}. Liability</h2>
      <p>
        Each party&apos;s liability under this DPA is subject to the limitations set out
        in the Terms of Use.
      </p>

      <h2>{n(14)}. Governing law</h2>
      <p>
        This DPA is governed by the laws of the <strong>{c.governingLawJurisdiction}</strong>.
      </p>

      <h2>Annex I — Details of processing</h2>
      <table>
        <tbody>
          <tr>
            <td>Controller</td>
            <td>Customer</td>
          </tr>
          <tr>
            <td>Processor</td>
            <td>
              {c.legalEntityName}
              {!c.hideBusinessIdentity && c.registeredAddressLine && (
                <>, {c.registeredAddressLine}</>
              )}
            </td>
          </tr>
          <tr>
            <td>Data subjects</td>
            <td>Customer&apos;s end users, contacts, prospects whose email addresses are submitted</td>
          </tr>
          <tr>
            <td>Categories of personal data</td>
            <td>Email addresses (raw + canonical); optionally domain / local-part metadata</td>
          </tr>
          <tr>
            <td>Special categories</td>
            <td>None — email addresses do not fall within GDPR Art. 9</td>
          </tr>
          <tr>
            <td>Nature of processing</td>
            <td>Canonicalisation, near-duplicate clustering, risk scoring, deliverability verification</td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>Fraud, bot-enumeration, and multi-accounting detection on Customer&apos;s signup flows</td>
          </tr>
          <tr>
            <td>Duration</td>
            <td>Lifetime of the Subscription + {c.retentionDaysAfterTermination} days deletion window</td>
          </tr>
        </tbody>
      </table>

      <h2>Annex II — Technical &amp; organisational measures (TOMs)</h2>
      <ul>
        <li>
          <strong>Encryption</strong>: TLS 1.3 in transit; AES-256 at rest (managed by AWS RDS).
        </li>
        <li>
          <strong>Tenant isolation</strong>: PostgreSQL Row-Level Security policies on every
          tenant-scoped table; per-request GUC + AsyncLocalStorage context; cross-tenant
          isolation e2e test gate in CI.
        </li>
        <li>
          <strong>Access controls</strong>: role-based access (super_admin / tenant_owner /
          tenant_member); password hashing with Argon2; per-API-key rate limiting; JWT
          access tokens with short TTL + refresh-token rotation.
        </li>
        <li>
          <strong>Network security</strong>: VPC isolation; security groups limiting DB access
          to application instances only; Cloudflare WAF + DDoS protection.
        </li>
        <li>
          <strong>Logging &amp; monitoring</strong>: API request logs (90 days); audit logs
          for all sensitive actions; CloudWatch metrics + alerts.
        </li>
        <li>
          <strong>Business continuity</strong>: RDS automated backups with point-in-time
          recovery (7-day retention); disaster recovery procedure documented.
        </li>
        <li>
          <strong>Vulnerability management</strong>: dependency scanning, regular patching,
          security-review skill applied on every release.
        </li>
        <li>
          <strong>Personnel</strong>: confidentiality agreements with all personnel having
          production access; least-privilege principle.
        </li>
        <li>
          <strong>Incident response</strong>: 72-hour breach notification SLA; documented
          response playbook.
        </li>
      </ul>

      {subprocessorsPage && (
        <>
          <h2>Annex III — Sub-processors</h2>
          <p>
            The current Sub-processor list is published at{' '}
            <Link to="/subprocessors">/subprocessors</Link> and incorporated here by reference.
          </p>
        </>
      )}
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default DpaPage;
